<template>
    <div class="music-style-management">
        <!-- 页面标题 -->
        <div class="page-header">
            <h1>歌曲风格管理</h1>
            <el-button type="primary" @click="showAddDialog">
                <el-icon>
                    <Plus />
                </el-icon>添加风格
            </el-button>
        </div>

        <!-- 搜索区域 -->
        <div class="search-area">
            <el-input v-model="searchQuery" placeholder="搜索歌曲风格" clearable @keyup.enter="handleSearch"
                class="search-input">
                <template #append>
                    <el-button @click="handleSearch">
                        <el-icon>
                            <Search />
                        </el-icon>
                    </el-button>
                </template>
            </el-input>
        </div>

        <!-- 列表区域 -->
        <div class="list-area">
            <!-- 加载状态 -->
            <div v-if="loading" class="loading-state">
                <el-skeleton :count="5" animated />
            </div>

            <!-- 空状态 -->
            <div v-else-if="filteredStyles.length === 0" class="empty-state">
                <el-icon class="empty-icon">
                    <Music />
                </el-icon>
                <h3>暂无歌曲风格</h3>
                <p>点击"添加风格"按钮创建第一个歌曲风格</p>
            </div>

            <!-- 列表内容 -->
            <el-table v-else :data="filteredStyles" border stripe highlight-current-row class="style-table">
                <el-table-column prop="id" label="ID" width="150" />
                <el-table-column prop="class" label="风格名称" min-width="150" />
                <el-table-column label="操作" width="120">
                    <template #default="scope">
                        <el-button v-if="canDelete()" size="mini" type="danger" @click="handleDelete(scope.row.id, scope.row.class)">
                            <el-icon>
                                <Delete />
                            </el-icon>删除
                        </el-button>
                    </template>
                </el-table-column>
            </el-table>
        </div>

        <!-- 分页控件 -->
        <div v-if="total > 0" class="pagination">
            <el-pagination background layout="total, sizes, prev, pager, next, jumper" :total="total"
                :page-sizes="[10, 20, 30, 50]" :page-size="pageSize" v-model:current-page="currentPage"
                @size-change="handleSizeChange" @current-change="handleCurrentChange" />
        </div>

        <!-- 添加风格对话框 -->
        <el-dialog v-model="addDialogVisible" title="添加歌曲风格" width="40%">
            <el-form :model="addForm" :rules="addRules" ref="addFormRef" label-width="100px">
                <el-form-item label="风格名称" prop="name">
                    <el-input v-model="addForm.name" placeholder="请输入风格名称" />
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="addDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="submitAddForm">确认添加</el-button>
            </template>
        </el-dialog>

        <!-- 删除确认对话框 -->
        <el-dialog v-model="deleteDialogVisible" title="确认删除" width="30%">
            <p>确定要删除风格 <span class="delete-item-name">{{ deleteItemName }}</span> 吗？</p>
            <p class="warning-text">删除后将无法恢复，请谨慎操作！</p>
            <template #footer>
                <el-button @click="deleteDialogVisible = false">取消</el-button>
                <el-button type="danger" @click="confirmDelete">确认删除</el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { canDelete } from '../../../util/permission'
import { ElMessage, ElMessageBox } from 'element-plus';
import { Search, Plus, Delete, Music } from '@element-plus/icons-vue';
import axios from 'axios';

// 状态管理
const styles = ref([]); // 风格列表数据
const total = ref(0); // 总记录数
const currentPage = ref(1); // 当前页码
const pageSize = ref(10); // 每页显示数量
const searchQuery = ref(''); // 搜索关键词
const loading = ref(false); // 加载状态
const addDialogVisible = ref(false); // 添加对话框显示状态
const deleteDialogVisible = ref(false); // 删除对话框显示状态
const deleteItemId = ref(null); // 待删除的ID
const deleteItemName = ref(''); // 待删除的名称

// 添加表单
const addForm = reactive({
    name: '',
});

// 添加表单验证规则
const addRules = reactive({
    name: [
        { required: true, message: '请输入风格名称', trigger: 'blur' },
        { min: 1, max: 50, message: '风格名称长度不能超过50个字符', trigger: 'blur' }
    ],
});

const addFormRef = ref(null);

// 列表数据现在由服务端分页 + 关键词查询返回，这里不再做本地过滤
// （原来是对「已经分页过的当前页」再过滤，等于只能搜到当前页里的内容）
const filteredStyles = computed(() => styles.value);

// 生命周期钩子
onMounted(() => {
    fetchStyles();
});

// 获取风格列表
const fetchStyles = async () => {
    loading.value = true;
    try {
        const response = await axios.get('/adminapi/class/list', {
            params: {
                page: currentPage.value,
                pageSize: pageSize.value,
                // 搜索词交给后端，否则第 2 页之后的分类永远搜不到
                query: searchQuery.value.trim()
            }
        });

        // console.log('获取风格列表成功:', response.data);
        styles.value = response.data.results || [];
        total.value = response.data.total || styles.value.length;
    } catch (error) {
        console.error('获取风格列表失败:', error);
        ElMessage.error('网络错误，请检查网络连接');
    } finally {
        loading.value = false;
    }
};

// 处理分页 - 每页数量变化
const handleSizeChange = (newSize) => {
    pageSize.value = newSize;
    currentPage.value = 1;
    fetchStyles();
};

// 处理分页 - 当前页变化
const handleCurrentChange = (newPage) => {
    currentPage.value = newPage;
    fetchStyles();
};

// 处理搜索
const handleSearch = () => {
    // 重置到第一页
    currentPage.value = 1;
    // 触发数据加载
    fetchStyles();
};

// 显示添加对话框
const showAddDialog = () => {
    // 重置表单
    addForm.name = '';
    addDialogVisible.value = true;
};

// 提交添加表单（关键修改点：请求参数改为 class）
const submitAddForm = async () => {
    // 表单验证
    const validateResult = await new Promise(resolve => {
        addFormRef.value.validate(valid => {
            resolve(valid);
        });
    });

    if (!validateResult) return;

    try {
        // 发送添加请求，参数改为 class（与接口和数据结构一致）
        const response = await axios.post('/adminapi/class/add', {
            class: addForm.name,
        });

        if (response.data.code === 200) {
            ElMessage.success('风格添加成功');
            addDialogVisible.value = false;
            // 刷新列表
            fetchStyles();
        } else {
            ElMessage.error(response.data.message || '添加失败');
        }
    } catch (error) {
        console.error('添加风格失败:', error);
        ElMessage.error('网络错误，请稍后重试');
    }
};

// 处理删除（关键修改点：传递 class 而非 name）
const handleDelete = (id, className) => {
    deleteItemId.value = id;
    deleteItemName.value = className;
    deleteDialogVisible.value = true;
};

// 确认删除
const confirmDelete = async () => {
    if (!deleteItemId.value) return;

    try {
        // 发送删除请求
        const response = await axios.delete(`/adminapi/class/${deleteItemId.value}`);

        if (response.data.code === 200) {
            ElMessage.success('风格删除成功');
            deleteDialogVisible.value = false;
            // 刷新列表
            fetchStyles();
        } else {
            ElMessage.error(response.data.message || '删除失败');
        }
    } catch (error) {
        console.error('删除风格失败:', error);
        ElMessage.error('网络错误，请稍后重试');
    }
};
</script>

<style scoped lang="scss">
/* 样式保持不变 */
.music-style-management {
    padding: 20px;
    max-width: 1200px;
    margin: 0 auto;

    .page-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 20px;

        h1 {
            font-size: 24px;
            font-weight: 600;
            color: #303133;
        }
    }

    .search-area {
        margin-bottom: 20px;
        display: flex;
        justify-content: flex-end;

        .search-input {
            width: 300px;
        }
    }

    .list-area {
        margin-bottom: 20px;

        .loading-state {
            padding: 30px 0;
            text-align: center;
        }

        .empty-state {
            text-align: center;
            padding: 50px 0;

            .empty-icon {
                font-size: 60px;
                color: #909399;
                margin-bottom: 20px;
            }

            h3 {
                font-size: 18px;
                color: #303133;
                margin-bottom: 10px;
            }

            p {
                color: #909399;
            }
        }

        .style-table {
            --el-table-header-text-color: #303133;
            --el-table-row-hover-bg-color: #f8f9fa;
        }
    }

    .pagination {
        display: flex;
        justify-content: center;
    }

    .delete-item-name {
        font-weight: bold;
        color: #f56c6c;
    }

    .warning-text {
        color: #f56c6c;
        margin-top: 10px;
    }
}
</style>